/**
 * ModulationEngine.js
 * Handles structural key changes and modulation suggestions.
 */

import { NOTES, normalizeKey } from '../../data/theoryConstants.js';

/**
 * Calculates a new key based on an interval from the root.
 * @param {string} rootKey - e.g., "C"
 * @param {number} semitones - e.g., 7 (Perfect 5th)
 * @returns {string} - New key
 */
export const transposeKey = (rootKey, semitones) => {
    const normalized = normalizeKey(rootKey);
    const idx = NOTES.indexOf(normalized);
    if (idx === -1) return rootKey;
    const newIdx = (idx + semitones + 12) % 12;
    return NOTES[newIdx];
};

/**
 * Suggests keys for each section in the structure based on standard progression forms.
 * @param {Array} structure - Array of section objects { type, bars, ... }
 * @param {string} globalKey - Main key of the song
 * @param {string} globalScale - 'major' or 'minor'
 * @param {boolean} allowModulation - Whether to allow key modulation (default: true)
 * @returns {Array} - Structure with suggested keys applied
 */
export const suggestSectionKeys = (structure, globalKey, globalScale, allowModulation = true) => {
    if (!structure) return [];

    return structure.map((section, idx) => {
        // If user has already manually set a key, preserve it
        if (section.key && section.scale) {
            return section;
        }

        if (!allowModulation) {
            return { ...section, key: globalKey, scale: globalScale };
        }

        let newKey = globalKey;
        let newScale = globalScale;

        const type = (section.type || 'default').toLowerCase();

        // Modulation Logic
        if (globalScale === 'major') {
            if (type.includes('chorus')) {
                // Lift to Dominant? Or stay tonic for stability.
                // Pop usually stays Tonic.
                // Epic/Orchestral might go to Dominant (V)
                if (Math.random() > 0.7) newKey = transposeKey(globalKey, 7);
            }
            else if (type.includes('bridge')) {
                // Relative Minor (vi) or Subdominant (IV)
                if (Math.random() > 0.5) {
                    newKey = transposeKey(globalKey, 9); // Relative Minor (A in C)
                    newScale = 'minor';
                } else {
                    newKey = transposeKey(globalKey, 5); // Subdominant (F in C)
                }
            }
            else if (type.includes('outro') || type.includes('solo')) {
                // Truck Driver's Gear Change? (+1 or +2 semitones)
                if (Math.random() > 0.8) newKey = transposeKey(globalKey, 2);
            }
        }
        else if (globalScale === 'minor') {
            if (type.includes('chorus')) {
                // Relative Major (bIII)
                if (Math.random() > 0.6) {
                    newKey = transposeKey(globalKey, 3); // Relative Major (Eb in C)
                    newScale = 'major';
                }
            }
            else if (type.includes('bridge')) {
                // Dominant (v or V)
                 newKey = transposeKey(globalKey, 7);
                 // Keep minor or harmonic minor
            }
        }

        return {
            ...section,
            key: newKey,
            scale: newScale
        };
    });
};

/**
 * Helper to check if a section needs modulation
 */
export const getScaleForSection = (section, globalContext) => {
    return {
        key: section.key || globalContext.rootKey,
        scale: section.scale || globalContext.scaleType
    };
};
